import {
  type KeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { DownOutlined, SearchOutlined, CheckOutlined } from '@ant-design/icons';
import { Tag } from '@/shared/ui/Tag';
import {
  UNCLASSIFIED_VIEW,
  viewMatchesQuery,
} from '../viewFiltering/viewTaxonomy';
import styles from './ViewFilter.module.css';

interface ViewFilterProps {
  /** Distinct view labels present in the study. */
  options: string[];
  /** Currently selected view labels. */
  selected: string[];
  onChange: (next: string[]) => void;
}

const LISTBOX_ID = 'view-filter-listbox';
const optionDomId = (index: number) => `view-filter-option-${index}`;

/**
 * Multi-select for view classifications (AP-441). The field shows the selected
 * views as removable pills; the dropdown is a searchable checkbox list (selected
 * first) with Clear / Select all. Toggling keeps the dropdown open so the reader
 * can pick several views in a row. Search matches full names and abbreviations.
 */
export function ViewFilter({ options, selected, onChange }: ViewFilterProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const wasOpen = useRef(open);

  const selectedSet = useMemo(() => new Set(selected), [selected]);

  // "No classified views" state: nothing real (non-Unclassified) to filter by,
  // so the field is disabled with a clear message instead of opening an
  // empty/near-empty dropdown.
  const disabled = useMemo(
    () => !options.some((o) => o !== UNCLASSIFIED_VIEW),
    [options]
  );
  const showPlaceholder = disabled || selected.length === 0;
  const placeholderText = disabled ? 'No classified views' : 'Search views';

  // Options matching the query, selected ones first (mirrors the design).
  const visible = useMemo(() => {
    const matched = options.filter((v) => viewMatchesQuery(v, query));
    return [
      ...matched.filter((v) => selectedSet.has(v)),
      ...matched.filter((v) => !selectedSet.has(v)),
    ];
  }, [options, selectedSet, query]);

  // Move focus into the search on open; restore it to the trigger on close so
  // keyboard/screen-reader users don't lose their place when the input unmounts.
  useEffect(() => {
    if (open) {
      inputRef.current?.focus();
    } else if (wasOpen.current) {
      triggerRef.current?.focus();
    }
    wasOpen.current = open;
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onDocMouseDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open]);

  // Keep the active option index within range as results change.
  useEffect(() => {
    setActiveIndex((i) =>
      visible.length === 0 ? -1 : Math.min(Math.max(i, 0), visible.length - 1)
    );
  }, [visible.length]);

  const toggle = (view: string) =>
    onChange(
      selectedSet.has(view)
        ? selected.filter((s) => s !== view)
        : [...selected, view]
    );
  const remove = (view: string) => onChange(selected.filter((s) => s !== view));
  const clearAll = () => onChange([]);
  const selectAll = () =>
    onChange(Array.from(new Set([...selected, ...visible])));

  const onFieldClick = (e: ReactMouseEvent) => {
    if (disabled) return;
    // Clicks on a pill's remove button shouldn't toggle the dropdown.
    if ((e.target as HTMLElement).closest('button')) return;
    setOpen((o) => !o);
  };
  const onFieldKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return;
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
    }
  };
  const onSearchKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, visible.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      if (activeIndex >= 0 && visible[activeIndex]) {
        e.preventDefault();
        toggle(visible[activeIndex]);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      if (query) setQuery('');
      else setOpen(false);
    }
  };

  return (
    <div className={styles.wrap} ref={wrapRef}>
      <span className={styles.label} id="view-filter-label">
        View Filter
      </span>
      <div
        ref={triggerRef}
        className={styles.field}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-disabled={disabled || undefined}
        aria-labelledby="view-filter-label"
        data-disabled={disabled || undefined}
        onClick={onFieldClick}
        onKeyDown={onFieldKeyDown}
        data-testid="view-filter-trigger"
      >
        <div className={styles.values}>
          {showPlaceholder ? (
            <span className={styles.placeholder}>{placeholderText}</span>
          ) : (
            selected.map((view) => (
              <Tag
                key={view}
                value={view}
                label={view}
                title={view}
                onRemove={remove}
                dataTestId={`view-pill-${view}`}
                className={styles.viewPill}
              />
            ))
          )}
        </div>
        <DownOutlined aria-hidden className={styles.chevron} />
      </div>

      {open && !disabled && (
        <div className={styles.popover}>
          <div className={styles.search}>
            <SearchOutlined aria-hidden className={styles.searchIcon} />
            <input
              ref={inputRef}
              type="text"
              role="combobox"
              aria-expanded
              aria-controls={LISTBOX_ID}
              aria-activedescendant={
                activeIndex >= 0 ? optionDomId(activeIndex) : undefined
              }
              aria-autocomplete="list"
              aria-label="Search views"
              className={styles.searchInput}
              placeholder="Search views"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onSearchKeyDown}
              autoComplete="off"
              data-testid="view-filter-input"
            />
          </div>

          <ul
            className={styles.list}
            role="listbox"
            id={LISTBOX_ID}
            aria-multiselectable="true"
          >
            {visible.length === 0 ? (
              <li role="status" className={styles.empty}>
                {options.length === 0
                  ? 'No classified views'
                  : 'No matching views'}
              </li>
            ) : (
              visible.map((view, i) => {
                const checked = selectedSet.has(view);
                return (
                  <li
                    key={view}
                    id={optionDomId(i)}
                    role="option"
                    aria-selected={checked}
                    className={`${styles.option} ${
                      i === activeIndex ? styles.optionActive : ''
                    }`}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      toggle(view);
                    }}
                    onMouseEnter={() => setActiveIndex(i)}
                    data-testid={`view-option-${view}`}
                  >
                    <span
                      className={`${styles.checkbox} ${
                        checked ? styles.checkboxChecked : ''
                      }`}
                    >
                      {checked && (
                        <CheckOutlined
                          aria-hidden
                          className={styles.checkIcon}
                        />
                      )}
                    </span>
                    {view}
                  </li>
                );
              })
            )}
          </ul>

          <div className={styles.footer}>
            <button
              type="button"
              className={styles.footerBtn}
              disabled={selected.length === 0}
              onMouseDown={(e) => e.preventDefault()}
              onClick={clearAll}
              data-testid="view-filter-clear"
            >
              Clear
            </button>
            <button
              type="button"
              className={styles.footerBtn}
              disabled={visible.length === 0}
              onMouseDown={(e) => e.preventDefault()}
              onClick={selectAll}
              data-testid="view-filter-select-all"
            >
              Select all
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
